/** biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO: will be removed */

import { type FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AutoComplete, type AutoCompleteProps, Form, type FormItemProps, Select, Spin, type TooltipProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useDebounce } from 'use-debounce';

import { isRequiredField } from '../../../utilz';
import { routeFileSelector } from '../../../utilz/i18n';
import formLabelToolTip from '../Common/formLabelToolTip';

const { Option } = Select;

export type Accessors = {
  label: string;
  value: string;
};

interface FormAutoCompleteProps extends AutoCompleteProps, Pick<FormItemProps, 'rules' | 'colon'> {
  label?: string;
  margin?: string;
  name?: string;
  options?: DefaultOptionType[];
  inverMode?: boolean;
  singleLineMode?: boolean;
  editMode?: boolean;
  asyncFetchMode?: boolean;
  asyncFunction?: (params: Record<string, unknown>) => Promise<{ data: any }>;
  searchParamName?: string;
  defaultParams?: Record<string, unknown>;
  accessors?: Accessors;
  CustomOptionsComponent?: FC<{ data: any; accessors: Accessors }>;
  hasError?: boolean;
  tooltip?: TooltipProps['title'];
  disableValidation?: boolean;
  loading?: boolean;
  mode?: string;
  onSearchValueFieldName?: string;
  onChange?: any;
  popupClassName?: string;
}

const FormAutoComplete: FC<FormAutoCompleteProps> = ({
  placeholder = false,
  label,
  options = [],
  defaultValue = undefined,
  margin = 'mb-16',
  loading = false,
  name = `testFiled-${Math.random()}`,
  rules = [],
  inverMode = false,
  singleLineMode = false,
  className = '',
  editMode = true,
  colon = false,
  disabled = false,
  onChange = () => {},
  asyncFetchMode = false,
  asyncFunction,
  searchParamName = 'SearchValue',
  defaultParams = {},
  value = undefined,
  accessors = { label: 'label', value: 'value' },
  CustomOptionsComponent,
  allowClear = true,
  hasError = false,
  onClear,
  mode,
  tooltip = false,
  onSearchValueFieldName = '1',
  popupClassName = '',
  suffixIcon = false,
  autoFocus = false,
  disableValidation = false,
  tabIndex,
  ...rest
}) => {
  const [dataOptions, setDataOptions] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [search, setSearch] = useState<string>();
  const [searchUsed, setSearchUsed] = useState(false);
  const [searchDebounced] = useDebounce(search, 600);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    onChange({ [onSearchValueFieldName]: search }, true);
  }, [search]);

  useEffect(() => {
    if (asyncFetchMode && searchDebounced) {
      setSearchUsed(true);
      fetchData(searchDebounced ? searchDebounced : undefined);
    }
  }, [searchDebounced]);

  const fetchData = async (search?: string) => {
    if (!asyncFunction) {
      return;
    }
    try {
      setDataLoading(true);
      const { data } = await asyncFunction({
        ...defaultParams,
        [searchParamName]: search,
      });
      setDataOptions(data ?? []);
      setDataLoading(false);
    } catch (error: any) {
      if (error.code !== 'ERR_CANCELED') {
        setDataOptions([]);
        setDataLoading(false);
      }
    }
  };

  const handleChange: AutoCompleteProps['onChange'] = (val) => {
    try {
      const data = asyncFetchMode ? dataOptions : options;
      onChange(data.find((item) => item[accessors.value] === val));
    } catch {
      onChange(undefined);
    }
  };

  return (
    <Form.Item
      tooltip={tooltip ? formLabelToolTip(tooltip) : null}
      className={`${inverMode ? 'invert-mode' : ''} ${margin} ${className} ${
        singleLineMode ? 'single-line-mode' : ''
      } form-input-container common-select-box-container ${
        !editMode ? 'edit-mode-off' : ''
      } auto-complete-form-input-container`}
      {...(label
        ? {
            label: (
              <span>
                {label}
                {rules.length > 0 && isRequiredField(rules) && !disableValidation && (
                  <span className="required">*</span>
                )}
              </span>
            ),
          }
        : {})}
      colon={colon}
      rules={disableValidation ? [] : rules}
      {...(!asyncFetchMode ? { name } : {})}
    >
      <AutoComplete
        autoFocus={autoFocus}
        popupClassName={popupClassName}
        id={name}
        defaultValue={mode === 'multiple' ? [] : defaultValue}
        {...(onClear ? { onClear } : {})}
        variant="borderless"
        disabled={disabled}
        placeholder={editMode ? placeholder : ''}
        className={`form-select-box  ${hasError ? 'ant-select-status-error' : ''}`}
        showSearch={editMode}
        onSelect={handleChange}
        searchValue={search}
        {...(!editMode
          ? {
              allowClear: false,
              clearIcon: false,
              open: false,
              suffixIcon: null,
            }
          : {})}
        // Async Search Logics
        {...(!asyncFetchMode
          ? {
              // @ts-expect-error
              filterOption: (input, option) => option?.children?.toLowerCase().includes(input.toLowerCase()),
              optionFilterProp: 'children',
            }
          : {
              filterOption: false,
              onSearch: (val) => setSearch(val),
              value,
            })}
        tabIndex={tabIndex}
        {...rest}
      >
        {!dataLoading ? (
          CustomOptionsComponent ? (
            (asyncFetchMode ? dataOptions : options)?.map((row, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO: fix
              <Option value={row[accessors.value]} key={idx}>
                {/* biome-ignore lint/suspicious/noArrayIndexKey: TODO: fix */}
                <CustomOptionsComponent data={row} accessors={accessors} key={idx} />
              </Option>
            ))
          ) : (
            (asyncFetchMode ? dataOptions : options)?.map((row, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO: fix
              <Option value={row[accessors.value]} key={idx}>
                {row[accessors.label]}
              </Option>
            ))
          )
        ) : (
          <Option disabled value="">
            <div className="d-flex justify-content-center p-20">
              <Spin />
            </div>
          </Option>
        )}
        {asyncFetchMode && !searchUsed ? (
          <Option disabled value="">
            <span className="f-12 text-grey-1">{t('translation:default.notification.emptySelect')}</span>
          </Option>
        ) : (
          <Fragment />
        )}
      </AutoComplete>
    </Form.Item>
  );
};

export default FormAutoComplete;
